import {
  DateTimeSelection,
  formatTimePickerDate,
  formatTimePickerString,
  getNextValidTimeSlot,
  isToday,
  startOfDay,
  TIME_PICKER_MINUTE_STEPS,
} from '@/experiences/sales-portal/blocs/utils/date-picker-calendar-utils';
import { useState } from 'react';

const snapPastToNextSlot = (date: Date, time: string): Date => {
  const selected = formatTimePickerDate(date, time);

  return isToday(date) && selected <= new Date()
    ? getNextValidTimeSlot(TIME_PICKER_MINUTE_STEPS)
    : selected;
};

/**
 * A reusable hook for a calendar day plus an `HH:mm` slot, to be used with any calendar component.
 * Picking a slot in the past resets it to the next valid 15 minute slot in the future;
 * when that slot rolls past midnight the selected day moves forward with it.
 *
 * `isSelectionValid` is true only when the combined timestamp was in the future at render time.
 * `getValidSelection` re-reads the clock when called, so a Save handler never commits a slot
 * that elapsed while the picker sat open; an elapsed slot is re-seeded to the next one instead.
 */
export const useDateTimePicker = (initial?: DateTimeSelection) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    initial?.date,
  );
  const [selectedTime, setSelectedTime] = useState<string>(
    formatTimePickerString(
      initial?.time ?? getNextValidTimeSlot(TIME_PICKER_MINUTE_STEPS),
    ),
  );

  const setDateAndTime = (dateTime: Date) => {
    setSelectedDate(startOfDay(dateTime));
    setSelectedTime(formatTimePickerString(dateTime));
  };

  const handleDateChange = (dates: Date[]) => {
    const nextDate = dates[0];

    if (!nextDate) {
      setSelectedDate(undefined);
      return;
    }

    setDateAndTime(snapPastToNextSlot(nextDate, selectedTime));
  };

  const handleTimeChange = (time: string) => {
    if (!selectedDate) {
      setSelectedTime(time);
      return;
    }

    setDateAndTime(snapPastToNextSlot(selectedDate, time));
  };

  const clear = () => {
    setSelectedDate(undefined);
    setSelectedTime(
      formatTimePickerString(getNextValidTimeSlot(TIME_PICKER_MINUTE_STEPS)),
    );
  };

  const selectedDateTime = selectedDate
    ? formatTimePickerDate(selectedDate, selectedTime)
    : undefined;
  const isSelectionValid = !!selectedDateTime && selectedDateTime > new Date();

  const getValidSelection = (): DateTimeSelection | undefined => {
    if (!selectedDate || !selectedDateTime) {
      return undefined;
    }

    if (selectedDateTime > new Date()) {
      return { date: selectedDate, time: selectedDateTime };
    }

    setDateAndTime(getNextValidTimeSlot(TIME_PICKER_MINUTE_STEPS));
    return undefined;
  };

  return {
    clear,
    getValidSelection,
    handleDateChange,
    handleTimeChange,
    isSelectionValid,
    selectedDate,
    selectedTime,
  };
};
